// §11.8 — one page, drawn.
//
// A page is a sheet at its own point size with every text run absolutely positioned on it, and
// zoom is a single CSS transform on the sheet — so the whole page scales as one thing and no
// layout is recomputed per run. Search highlighting splits a run's text into spans; nothing about
// the geometry changes, which is what keeps a highlight from nudging the line it is in.
import type { CSSProperties } from 'react';
import { COPY } from '@/copy';
import type { PdfPage, PdfTextRun } from './document';
import './pdf-page.css';

export interface PageMatch {
  readonly runIndex: number;
  readonly start: number;
  readonly length: number;
  /** Its position in the document-wide match list, so the active one can be marked. */
  readonly ordinal: number;
}

/**
 * Baselines: PDF places text on its baseline, CSS places a line box by its top. 0.8 em is the
 * usual ascent of a text face, and being a fraction of the run's own size it stays right at every
 * zoom.
 */
const ASCENT = 0.8;

function runStyle(run: PdfTextRun): CSSProperties {
  return {
    left: `${run.x}px`,
    top: `${run.y - run.size * ASCENT}px`,
    fontSize: `${run.size}px`,
    fontFamily: run.style.family,
    fontWeight: run.style.bold ? 700 : 400,
    fontStyle: run.style.italic ? 'italic' : 'normal',
    ...(run.angle === 0
      ? {}
      : { transform: `rotate(${run.angle}rad)`, transformOrigin: `0 ${ASCENT * 100}%` }),
  };
}

function RunText({ run, matches, active }: { run: PdfTextRun; matches: readonly PageMatch[]; active: number }) {
  if (matches.length === 0) return <>{run.text}</>;
  const parts: Array<{ text: string; hit: number | null }> = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start > cursor) parts.push({ text: run.text.slice(cursor, match.start), hit: null });
    parts.push({ text: run.text.slice(match.start, match.start + match.length), hit: match.ordinal });
    cursor = match.start + match.length;
  }
  if (cursor < run.text.length) parts.push({ text: run.text.slice(cursor), hit: null });

  return (
    <>
      {parts.map((part, index) =>
        part.hit === null ? (
          <span key={index}>{part.text}</span>
        ) : (
          <mark
            key={index}
            className="pdf-hit"
            data-active={String(part.hit === active)}
            data-testid={part.hit === active ? 'pdf-hit-active' : 'pdf-hit'}
          >
            {part.text}
          </mark>
        ),
      )}
    </>
  );
}

export function PdfPageView({
  page,
  zoom,
  matches,
  activeMatch,
}: {
  page: PdfPage;
  zoom: number;
  matches?: readonly PageMatch[];
  activeMatch?: number;
}) {
  const byRun = new Map<number, PageMatch[]>();
  for (const match of matches ?? []) {
    const list = byRun.get(match.runIndex);
    if (list) list.push(match);
    else byRun.set(match.runIndex, [match]);
  }

  return (
    <div
      className="pdf-page"
      data-testid={`pdf-page-${page.index + 1}`}
      data-page={page.index + 1}
      style={{
        inlineSize: `${page.width * zoom}px`,
        blockSize: `${page.height * zoom}px`,
      }}
    >
      <div
        className="pdf-sheet"
        style={{
          inlineSize: `${page.width}px`,
          blockSize: `${page.height}px`,
          transform: `scale(${zoom})`,
        }}
      >
        {page.runs.map((run, index) => (
          <span key={index} className="pdf-run" style={runStyle(run)}>
            <RunText run={run} matches={byRun.get(index) ?? []} active={activeMatch ?? -1} />
          </span>
        ))}
        {page.runs.length === 0 ? (
          // D-170 — the honest answer for a scan. A blank sheet would look like a bug.
          <p className="pdf-page-empty" data-testid="pdf-page-empty">
            {COPY.pdf.noTextOnPage}
          </p>
        ) : null}
      </div>
    </div>
  );
}
